import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Message, Placeholder } from 'rsuite';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import SendSurveyModal from '../components/surveys/SendSurveyModal';
import SurveyLinkButton from '../components/surveys/SurveyLinkButton';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../utils/toast';
import { useCreateSurvey, useManagedSurveys, useMySurveys } from '../hooks/useSurveys';
import { STATE_LABELS, formatDate, scheduleLabel } from '../lib/surveys';
import './SurveysPage.css';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

const MY_STATUS = {
  not_started: { label: 'Not started', action: 'Start' },
  in_progress: { label: 'In progress', action: 'Continue' },
  submitted: { label: 'Submitted', action: 'View or change' },
  anonymousSubmitted: { label: 'Submitted', action: 'View' },
};

/**
 * Surveys. Everyone sees the surveys sent to them, to take or look back on. People who can create
 * surveys also see every survey below that, start new ones and open any of them in the builder.
 */
export default function SurveysPage() {
  const auth = useAuth();
  const canCreate = auth.hasPermission('create_surveys');
  const navigate = useNavigate();
  const mine = useMySurveys();
  const { data, isLoading, error } = useManagedSurveys({ enabled: canCreate });
  const createSurvey = useCreateSurvey();
  // Until someone picks a filter, show open surveys, or all of them when none are open.
  const [pickedFilter, setFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(null);

  const surveys = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const counts = useMemo(
    () => surveys.reduce((acc, s) => ({ ...acc, [s.state]: (acc[s.state] || 0) + 1 }), {}),
    [surveys]
  );
  const filter = pickedFilter ?? (counts.open ? 'open' : 'all');
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return surveys.filter((s) => (filter === 'all' || s.state === filter)
      && (!needle || `${s.title} ${s.description}`.toLowerCase().includes(needle)));
  }, [surveys, filter, search]);

  const startNew = async () => {
    try {
      const survey = await createSurvey.mutateAsync({ title: 'Untitled survey' });
      navigate(`/surveys/${survey.id}/edit`);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to start a survey'));
    }
  };

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Surveys' }]}
        icon="bi-ui-checks"
        title="Surveys"
        actions={canCreate ? (
          <button type="button" className="bdc-btn bdc-btn--primary" onClick={startNew} disabled={createSurvey.isPending}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            {createSurvey.isPending ? 'Starting…' : 'New survey'}
          </button>
        ) : null}
      />

      <MySurveys query={mine} onOpen={(id) => navigate(`/surveys/${id}`)} />

      {canCreate ? (
        <Section title="Manage surveys" count={surveys.length}>
          <div className="svy-toolbar">
            <div className="svy-filters" role="tablist" aria-label="Show surveys">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.value}
                  className={`svy-filter${filter === f.value ? ' is-active' : ''}`}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                  <span className="svy-filter__count">{f.value === 'all' ? surveys.length : counts[f.value] || 0}</span>
                </button>
              ))}
            </div>
            <div className="svy-search">
              <i className="bi bi-search" aria-hidden="true" />
              <Input value={search} onChange={setSearch} placeholder="Search surveys" aria-label="Search surveys" />
            </div>
          </div>

          {error ? (
            <Message type="error" showIcon>{String(error?.message || error)}</Message>
          ) : isLoading ? (
            <Placeholder.Grid rows={3} columns={3} active />
          ) : surveys.length === 0 ? (
            <div className="svy-empty">
              <i className="bi bi-ui-checks" aria-hidden="true" />
              <h2>No surveys yet</h2>
              <p>
                Build a survey with text, links, images and questions (dropdowns, text, star ratings, rankings and
                signatures), send it to everyone or to the people you pick, and set a date it closes.
              </p>
              <button type="button" className="bdc-btn bdc-btn--primary" onClick={startNew} disabled={createSurvey.isPending}>
                <i className="bi bi-plus-lg" aria-hidden="true" />
                New survey
              </button>
            </div>
          ) : shown.length === 0 ? (
            <div className="svy-empty svy-empty--small">No surveys match.</div>
          ) : (
            <div className="svy-grid">
              {shown.map((survey) => (
                <SurveyCard key={survey.id} survey={survey} onOpen={() => navigate(`/surveys/${survey.id}/edit`)} onSend={() => setSending(survey)} onResponses={() => navigate(`/surveys/${survey.id}/responses`)} />
              ))}
            </div>
          )}
        </Section>
      ) : null}
      {sending ? <SendSurveyModal survey={sending} onClose={() => setSending(null)} /> : null}
    </div>
  );
}

// The surveys sent to me: open ones to take, and closed ones I answered.
function MySurveys({ query, onOpen }) {
  const list = Array.isArray(query.data) ? query.data : [];
  const waiting = list.filter((s) => s.state === 'open' && s.myStatus !== 'submitted').length;
  return (
    <Section title="For you" count={list.length} note={waiting ? `${waiting} waiting on you` : null}>
      {query.error ? (
        <Message type="error" showIcon>{String(query.error?.message || query.error)}</Message>
      ) : query.isLoading ? (
        <Placeholder.Paragraph rows={3} active />
      ) : list.length === 0 ? (
        <div className="svy-empty svy-empty--small">
          No surveys for you right now. When one is sent to you it shows up here.
        </div>
      ) : (
        <div className="svy-grid">
          {list.map((survey) => (
            <MySurveyCard key={survey.id} survey={survey} onOpen={() => onOpen(survey.id)} />
          ))}
        </div>
      )}
    </Section>
  );
}

function MySurveyCard({ survey, onOpen }) {
  const status = survey.anonymous && survey.myStatus === 'submitted'
    ? MY_STATUS.anonymousSubmitted
    : MY_STATUS[survey.myStatus] || MY_STATUS.not_started;
  const closed = survey.state === 'closed';
  return (
    <button type="button" className={`svy-card svy-card--mine svy-card--${survey.myStatus}${closed ? ' is-closed' : ''}`} onClick={onOpen}>
      <div className="svy-card__top">
        <span className="svy-card__badges">
          <span className={`svy-mystatus svy-mystatus--${survey.myStatus}`}>
            {survey.myStatus === 'submitted' ? <i className="bi bi-check-circle-fill" aria-hidden="true" /> : null}
            {status.label}
          </span>
          {survey.anonymous ? (
            <span className="svy-anon" title="Anonymous">
              <i className="bi bi-incognito" aria-hidden="true" />
              Anonymous
            </span>
          ) : null}
        </span>
        {survey.closesOn || closed ? (
          <span className={`svy-card__closes${!closed && survey.myStatus !== 'submitted' ? ' is-due' : ''}`}>
            <i className="bi bi-calendar-x" aria-hidden="true" />
            {closed ? 'Closed' : `Due ${formatDate(survey.closesOn)}`}
          </span>
        ) : null}
      </div>
      <h3 className="svy-card__title">{survey.title || 'Untitled survey'}</h3>
      {survey.description ? <p className="svy-card__desc">{survey.description}</p> : null}
      <div className="svy-card__meta">
        <span>
          <i className="bi bi-question-circle" aria-hidden="true" />
          {survey.questionCount} question{survey.questionCount === 1 ? '' : 's'}
        </span>
        {survey.submittedAt ? (
          <span>
            <i className="bi bi-send-check" aria-hidden="true" />
            Sent {formatDate(String(survey.submittedAt).slice(0, 10))}
          </span>
        ) : null}
      </div>
      <span className="svy-card__cta">
        {closed ? 'View your answers' : status.action}
        <i className="bi bi-arrow-right" aria-hidden="true" />
      </span>
    </button>
  );
}

function SurveyCard({ survey, onOpen, onSend, onResponses }) {
  const rate = survey.recipientCount ? Math.round((survey.responseCount / survey.recipientCount) * 100) : 0;
  const linkAccess = survey.allowLinkResponses || survey.audience === 'link';
  return (
    <article className={`svy-card svy-card--managed svy-card--${survey.state}`} aria-label={survey.title || 'Untitled survey'}>
      <div className="svy-card__top">
        <span className="svy-card__badges">
          <span className={`svy-state svy-state--${survey.state}`}>{STATE_LABELS[survey.state]}</span>
          {survey.anonymous ? (
            <span className="svy-anon" title="Anonymous">
              <i className="bi bi-incognito" aria-hidden="true" />
              Anonymous
            </span>
          ) : null}
          {survey.schedule ? (
            <span className="svy-repeat" title={`Next round ${formatDate(survey.schedule.nextRunOn)}`}>
              <i className="bi bi-arrow-repeat" aria-hidden="true" />
              {scheduleLabel(survey.schedule.everyMonths)}
            </span>
          ) : null}
        </span>
        {survey.state === 'open' ? (
          <div className="svy-card__actions">
            <button type="button" className="bdc-btn bdc-btn--sm bdc-btn--primary" onClick={onSend}>
              <i className="bi bi-send" aria-hidden="true" />
              Send out
            </button>
            <SurveyLinkButton survey={survey} className="bdc-btn--sm" />
          </div>
        ) : survey.state === 'closed' ? (
          <div className="svy-card__actions">
            <button type="button" className="bdc-btn bdc-btn--sm" onClick={onResponses}>
              <i className="bi bi-bar-chart" aria-hidden="true" />
              View responses
            </button>
          </div>
        ) : null}
      </div>
      <button type="button" className="svy-card__open" onClick={onOpen} aria-label={`Edit ${survey.title || 'Untitled survey'}`}>
      <h3 className="svy-card__title">{survey.title || 'Untitled survey'}</h3>
      {survey.description ? <p className="svy-card__desc">{survey.description}</p> : null}
      <div className="svy-card__meta">
        {survey.closesOn ? (
          <span className="svy-card__closes">
            <i className="bi bi-calendar-x" aria-hidden="true" />
            {survey.state === 'closed' ? 'Closed' : 'Closes'} {formatDate(survey.closesOn)}
          </span>
        ) : null}
        <span>
          <i className="bi bi-question-circle" aria-hidden="true" />
          {survey.questionCount} question{survey.questionCount === 1 ? '' : 's'}
        </span>
        <span>
          <i className="bi bi-people" aria-hidden="true" />
          {survey.audience === 'all' ? 'All Employees' : `${survey.recipientCount} ${survey.recipientCount === 1 ? 'invitee' : 'invitees'}`}
        </span>
        {linkAccess ? <span><i className="bi bi-link-45deg" aria-hidden="true" />Anyone with the link</span> : null}
        {survey.schedule ? (
          <span>
            <i className="bi bi-calendar-event" aria-hidden="true" />
            Next round {formatDate(survey.schedule.nextRunOn)}
          </span>
        ) : null}
      </div>
      {survey.state !== 'draft' && linkAccess && survey.audience !== 'all' ? (
        <div className="svy-card__foot">{survey.responseCount} {survey.responseCount === 1 ? 'response' : 'responses'}</div>
      ) : survey.state !== 'draft' ? (
        <div className="svy-card__progress">
          <div className="svy-card__progress-text">
            <strong>{survey.responseCount}</strong> of {survey.recipientCount} responded
            <span>{rate}%</span>
          </div>
          <div className="svy-card__track">
            <span style={{ width: `${Math.min(100, rate)}%` }} />
          </div>
        </div>
      ) : (
        <div className="svy-card__foot">Edited {formatDate(String(survey.updatedAt).slice(0, 10))}{survey.updatedByName ? ` by ${survey.updatedByName}` : ''}</div>
      )}
      </button>
    </article>
  );
}
